// src/pages/Home.tsx
import { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";

import Hero from "../components/Hero";
import KakaoChatButton from "../components/KakaoChatButton";

import MobileIntroHome from "./MobileIntroHome";
import { isMobileLike } from "../lib/isMobile";
import HomeNoticeModal from "../components/HomeNoticeModal";


// ✅ 4단계 플로우 아이콘
import flowDoc from "../assets/flow-icons/자료.png";
import flowReview from "../assets/flow-icons/검토.png";
import flowPay from "../assets/flow-icons/결제.png";
import flowSend from "../assets/flow-icons/송금.png";

// ✅ 카드사 로고
import logoBC from "../assets/card-logos/bc.png";
import logoCiti from "../assets/card-logos/citi.png";
import logoHana from "../assets/card-logos/hana.png";
import logoHyundai from "../assets/card-logos/hyundai.png";
import logoJeju from "../assets/card-logos/jeju.png";
import logoJeonbuk from "../assets/card-logos/jeonbuk.png";
import logoKakaoBank from "../assets/card-logos/kakaobank.png";
import logoKB from "../assets/card-logos/kb.png";
import logoKBank from "../assets/card-logos/kbank.png";
import logoKwangju from "../assets/card-logos/kwangju.png";
import logoLotte from "../assets/card-logos/lotte.png";
import logoMG from "../assets/card-logos/mg.png";
import logoNonghyup from "../assets/card-logos/nonghyup.png";
import logoPost from "../assets/card-logos/post.png";
import logoSamsung from "../assets/card-logos/samsung.png";
import logoSave from "../assets/card-logos/save.png";
import logoSinhan from "../assets/card-logos/sinhan.png";
import logoSinhyup from "../assets/card-logos/sinhyup.png";
import logoSoohyup from "../assets/card-logos/soohyup.png";
import logoWoori from "../assets/card-logos/woori.png";

const API_BASE = (import.meta.env.VITE_API_BASE || "").replace(/\/$/, "");



// 상단 4단계 플로우
const FLOW_ITEMS: Array<{ title: string; description: string; icon: string }> = [
  { title: "증빙자료 등록", description: "계약/거래 관련 서류를 업로드합니다.", icon: flowDoc },
  { title: "자료 검토", description: "관리자가 서류를 검토합니다.", icon: flowReview },
  { title: "결제 등록", description: "승인된 건에 대해 카드 결제가 준비됩니다.", icon: flowPay },
  { title: "10초 송금", description: "카드사 승인 즉시 빠르게 정산됩니다.", icon: flowSend },
];

const INTRO_POINTS = [
  <>
    <span className="text-orange-500 font-semibold">주류 거래 대금</span>을{" "}
    <span className="text-orange-500 font-semibold">카드로 결제</span>하는 서비스
  </>,
  <>
    <span className="text-orange-500 font-semibold">현금 자금 흐름</span>을{" "}
    <span className="text-orange-500 font-semibold">안정적으로 관리</span> 가능
  </>,
  <>
    <span className="text-orange-500 font-semibold">단순 송금</span>이 아닌{" "}
    <span className="text-orange-500 font-semibold">실제 계약 기반</span> 서비스
  </>,
  <>
    수수료는 <span className="text-orange-500 font-semibold">업계 최저수수료 3.7%</span>이며,{" "}
    <span className="text-orange-500 font-semibold">부가세는 별도</span>로 추가됩니다.
  </>,
];

const TESTIMONIALS = [
  {
    quote: "외상 정산이 한 번에 정리돼서 월말 부담이 줄었어요.",
    name: "40대 도매상",
    tag: "외상 정산",
  },
  {
    quote: "카드 결제로 결제 흐름이 투명해지고 증빙 관리가 쉬워졌습니다.",
    name: "30대 매장 운영자",
    tag: "결제 관리",
  },
  {
    quote: "승인 후 바로 정산돼서 재고 회전이 훨씬 빠릅니다.",
    name: "50대 유통업 종사자",
    tag: "빠른 정산",
  },
];

const FAQ_ITEMS = [
  {
    q: "서비스 이용 수수료는 얼마인가요?",
    a: "서비스 이용료는 업계 최저수수료 3.7%**이며, 부가세는 별도로 추가됩니다.",
  },
  {
    q: "어떤 서류가 필요한가요?",
    a: "거래품목별로 결제등록할 때 작성되어 있습니다. 제출하신 서류가 미비한 경우, 추가 증빙 자료를 요청드릴 수 있습니다.",
  },
  {
    q: "계약 승인은 얼마나 걸리나요?",
    a: "업로드하신 계약서 검토 후 승인 여부를 안내드립니다. 일반적으로 약 30분~1시간 내외이며, 서류 확인이 필요한 경우 시간이 더 걸릴 수 있습니다.",
  },
  {
    q: "카드 할부나 무이자 혜택도 이용할 수 있나요?",
    a: "네, 가능합니다. 무이자 할부 기간은 ‘무이자 할부 안내’에서 확인하실 수 있습니다.",
  },
];

type InstallmentRow = {
  id?: number;
  key: string;
  name: string;
  term: string;
  enabled?: boolean;
  sort_order?: number;
  partial_text?: string | null;
  partial_note?: string | null;
  partialText?: string | null;
  partialNote?: string | null;
};

// 로고 매핑
const CARD_LOGOS: Record<string, string> = {
  bc: logoBC,
  kb: logoKB,
  sinhan: logoSinhan,
  samsung: logoSamsung,
  lotte: logoLotte,
  woori: logoWoori,
  nonghyup: logoNonghyup,
  jeonbuk: logoJeonbuk,
  kwangju: logoKwangju,
  hyundai: logoHyundai,
  citi: logoCiti,
  hana: logoHana,
  jeju: logoJeju,
  kakaobank: logoKakaoBank,
  kbank: logoKBank,
  mg: logoMG,
  post: logoPost,
  save: logoSave,
  sinhyup: logoSinhyup,
  soohyup: logoSoohyup,
};

type InstallmentCard = InstallmentRow & { logo?: string };

function normalizeTermDisplay(term: string): string {
  const t = (term || "").trim();
  if (!t) return "";
  if (t.includes("불가")) return t;
  if (/\d/.test(t) && !/개월/.test(t)) return `${t}개월`;
  return t;
}

function normalizePartialText(row: any): string {
  const raw =
    row?.partial_text ??
    row?.partialText ??
    row?.partial_text?.toString?.() ??
    row?.partialText?.toString?.();
  return (raw ? String(raw) : "").trim();
}

function normalizePartialNote(row: any): string {
  const raw =
    row?.partial_note ??
    row?.partialNote ??
    row?.partial_note?.toString?.() ??
    row?.partialNote?.toString?.();
  return (raw ? String(raw) : "").trim();
}

/**
 * ✅ 루트 홈 분기
 * - 앱(WebView): 인트로 생략 → /m/home로 즉시 이동
 * - 모바일/크롬 모바일모드: MobileIntroHome
 * - PC: 기존 홈(DesktopHome)
 */
export default function Home() {
  const mobile = isMobileLike();

  const isApp =
    typeof window !== "undefined" &&
    /JULYUPAY_APP/.test(window.navigator.userAgent || "");

  if (isApp) {
    return <Navigate to="/m/home" replace />;
  }

  return mobile ? <MobileIntroHome /> : <DesktopHome />;
}

/**
 * ✅ PC 전용 홈
 */
function DesktopHome() {
  const nav = useNavigate();

  const [installments, setInstallments] = useState<InstallmentCard[]>([]);
  const [isApp, setIsApp] = useState(false);
  const [partialOpen, setPartialOpen] = useState(false);
  const [testimonialIndex, setTestimonialIndex] = useState(0);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const ua = window.navigator.userAgent || "";
      setIsApp(/JULYUPAY_APP/.test(ua));
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const url = API_BASE ? `${API_BASE}/api/installments` : "/api/installments";
        const res = await fetch(url);
        if (!res.ok) throw new Error("failed");
        const data = await res.json();

        const raw =
          data?.items ||
          data?.installments ||
          data?.list ||
          data?.data ||
          data?.cards ||
          [];

        if (!Array.isArray(raw)) {
          setInstallments([]);
          return;
        }

        const mapped: InstallmentCard[] = raw
          .map((row: any) => {
            const key = String(row.key || "").trim();
            const name = String(row.name || "").trim();
            const term = String(row.term || "").trim();
            if (!key || !name) return null;

            const partial_text = normalizePartialText(row) || null;
            const partial_note = normalizePartialNote(row) || null;

            return {
              id: row.id,
              key,
              name,
              term,
              enabled: row.enabled,
              sort_order: row.sort_order,
              partial_text,
              partial_note,
              logo: CARD_LOGOS[key],
            } as InstallmentCard;
          })
          .filter((c): c is InstallmentCard => !!c && !!c.logo);

        setInstallments(mapped);
      } catch (e) {
        console.error("load installments error", e);
        setInstallments([]);
      }
    };
    load();
  }, []);

  const partialCards = installments.filter((c) => (c.partial_text || "").trim().length > 0);

  return (
    <div className="-mx-4 sm:mx-0 bg-slate-50 min-h-screen">
      {/* 상단 히어로 */}
      <Hero />

      {/* 앱 전용: 이벤트/프로모션/초대 배너 */}
      {isApp && (
        <section className="bg-slate-50 pb-10 pt-4">
          <div className="mx-auto max-w-md px-4 space-y-4">
            <div className="rounded-2xl bg-[#f97316] px-4 py-6 text-white shadow-md">
              <div className="text-sm font-semibold">이벤트 배너 영역</div>
              <p className="mt-1 text-xs text-white/80">
                나중에 홈 상단 첫 번째 이미지(이벤트 배너)가 들어갈 자리입니다.
              </p>
            </div>

            <div className="rounded-2xl bg-[#FFE062] px-4 py-6 shadow-md">
              <div className="text-sm font-semibold text-slate-900">(노란 프로모션 배너 자리)</div>
              <p className="mt-1 text-xs text-slate-700">
                서비스 이용료, 수수료 안내 등 프로모션 배너가 들어갈 공간입니다.
              </p>
            </div>

            <div className="rounded-2xl bg-[#C7F7D4] px-4 py-6 shadow-md">
              <div className="text-sm font-semibold text-slate-900">(초대 혜택 배너 자리)</div>
              <p className="mt-1 text-xs text-slate-700">
                친구 초대 시 포인트 적립, 수수료 할인 등 혜택 안내 영역입니다.
              </p>
            </div>
          </div>
        </section>
      )}

      

      {/* 이하 내용은 원본 그대로 */}
      <section className="bg-slate-50 py-16">
        <div className="mx-auto max-w-6xl px-4 space-y-8">
          <div className="rounded-[32px] border border-slate-200 bg-white/80 px-6 py-10 shadow-[0_20px_60px_-40px_rgba(15,23,42,0.35)]">
            <div className="flex items-center justify-between gap-4">
              <h2 className="text-xl sm:text-2xl font-extrabold text-slate-900">주류페이란?</h2>
              <span className="rounded-full border border-slate-200 px-4 py-1 text-xs font-semibold text-slate-600">
                서비스 소개
              </span>
            </div>

            <ul className="mt-6 grid gap-3">
              {INTRO_POINTS.map((text, idx) => (
                <li key={idx} className="flex items-start gap-3 rounded-2xl bg-slate-50 px-4 py-3">
                  <span className="mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#ea580c] text-white text-xs font-bold">
                    ✓
                  </span>
                  <span className="text-sm text-slate-700 leading-relaxed">{text}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-[32px] bg-gradient-to-b from-slate-50 to-slate-100 px-6 py-10 shadow-inner">
            <div className="mx-auto max-w-3xl">
              <div className="rounded-3xl bg-[#ea580c] px-6 py-8 text-white shadow-[0_18px_50px_-30px_rgba(15,23,42,0.45)]">
                <p className="text-lg sm:text-xl font-semibold leading-relaxed">
                  “{TESTIMONIALS[testimonialIndex].quote}”
                </p>
              </div>

              <div className="mt-6 text-center">
                <div className="text-sm font-semibold text-slate-900">
                  {TESTIMONIALS[testimonialIndex].name}
                </div>
                <div className="mt-1 text-xs text-slate-500">
                  {TESTIMONIALS[testimonialIndex].tag}
                </div>
              </div>

              <div className="mt-6 flex items-center justify-center gap-3">
                <button
                  type="button"
                  onClick={() =>
                    setTestimonialIndex((v) => (v - 1 + TESTIMONIALS.length) % TESTIMONIALS.length)
                  }
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                  aria-label="이전 후기"
                >
                  ←
                </button>
                <button
                  type="button"
                  onClick={() => setTestimonialIndex((v) => (v + 1) % TESTIMONIALS.length)}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                  aria-label="다음 후기"
                >
                  →
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-16">
        <div className="mx-auto max-w-6xl px-4 space-y-10">
          <div className="rounded-3xl border border-slate-200 bg-slate-50/80 px-6 py-10">
            <h2 className="text-center text-xl sm:text-2xl font-bold text-slate-900">
              정말 쉽고 간편한 결제 시스템
            </h2>

            <div className="mt-8 flex flex-col items-center gap-8">
              <div className="flex flex-col items-center gap-8 sm:flex-row sm:gap-10">
                {FLOW_ITEMS.map((item, idx) => (
                  <div key={item.title} className="flex flex-col items-center text-center">
                    <div className="flex items-center gap-4">
                      <div className="flex flex-col items-center">
                        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-b from-[#ff8a1f] to-[#ea580c] shadow-lg">
                          <img
                            src={item.icon}
                            alt={item.title}
                            className="h-9 w-9 object-contain"
                            draggable={false}
                            loading="lazy"
                          />
                        </div>

                        <div className="mt-3 text-sm font-semibold text-slate-900">{item.title}</div>
                      </div>

                      {idx < FLOW_ITEMS.length - 1 && (
                        <span className="hidden items-center gap-1 sm:flex">
                          <div className="h-[2px] w-8 rounded-full bg-[#fed7aa]" />
                          <span className="text-2xl text-[#f97316]">➜</span>
                        </span>
                      )}
                    </div>

                    {idx < FLOW_ITEMS.length - 1 && (
                      <span className="mt-4 flex items-center justify-center sm:hidden">
                        <div className="h-6 w-[2px] rounded-full bg-[#fed7aa]" />
                        <span className="ml-1 text-xl text-[#f97316]">↓</span>
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-slate-50/80 px-4 py-6 sm:px-8 sm:py-8">
            <h3 className="text-center text-xl sm:text-2xl font-bold text-slate-900">무이자 할부 혜택</h3>
            <p className="mt-2 text-center text-sm text-slate-600">
              카드사 별 최대7개월까지 신용카드 무이자 할부
            </p>

            <div className="mt-8 grid gap-4 sm:gap-5 sm:grid-cols-2">
              {installments.map((card) => {
                const displayTerm = normalizeTermDisplay(card.term);
                return (
                  <div
                    key={card.key}
                    className="flex items-center gap-4 rounded-2xl bg-white px-4 py-3 shadow-sm"
                  >
                    <div className="flex h-10 w-10 sm:h-12 sm:w-12 items-center justify-center rounded-full bg-slate-50">
                      {card.logo && (
                        <img
                          src={card.logo}
                          alt={card.name}
                          className="h-7 w-7 sm:h-9 sm:w-9 object-contain"
                          draggable={false}
                          loading="lazy"
                        />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="text-sm sm:text-base font-semibold text-slate-900">{card.name}</div>
                    </div>
                    <div
                      className={`text-base sm:text-lg font-bold tracking-tight ${
                        displayTerm === "할부 불가" ? "text-red-500" : "text-slate-900"
                      }`}
                    >
                      {displayTerm}
                    </div>
                  </div>
                );
              })}

              {!installments.length && (
                <div className="col-span-1 sm:col-span-2 text-center text-xs text-slate-400">
                  무이자 할부 정보가 아직 설정되지 않았습니다.
                </div>
              )}
            </div>

            {partialCards.length > 0 && (
              <>
                <div className="mt-8 h-px w-full bg-slate-200/70" />
                <div className="mt-6">
                  <div className="flex flex-col items-center gap-2 text-center">
                    <div className="text-base sm:text-lg font-extrabold text-slate-900">
                      부분 무이자 할부 안내
                    </div>
                    <p className="text-xs sm:text-sm text-slate-600">
                      카드사별 “고객부담(회차)” 조건이 있는 부분 무이자 혜택을 안내합니다.
                    </p>
                    <button
                      type="button"
                      onClick={() => setPartialOpen((v) => !v)}
                      aria-expanded={partialOpen}
                      className="rounded-full border border-slate-200 bg-white px-4 py-2 text-xs sm:text-sm font-semibold text-slate-700 hover:bg-slate-50"
                    >
                      {partialOpen ? "안내 접기" : "안내 보기"}
                    </button>
                  </div>

                  {partialOpen && (
                    <div className="mt-6 space-y-3">
                      {partialCards.map((card) => {
                        const text = (card.partial_text || "").trim();
                        const note = (card.partial_note || "").trim();

                        return (
                          <div
                            key={`partial-${card.key}`}
                            className="rounded-2xl border border-slate-200 bg-white px-4 py-4 shadow-sm"
                          >
                            <div className="flex items-start gap-3">
                              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-50">
                                {card.logo && (
                                  <img
                                    src={card.logo}
                                    alt={card.name}
                                    className="h-7 w-7 object-contain"
                                    draggable={false}
                                    loading="lazy"
                                  />
                                )}
                              </div>

                              <div className="flex-1">
                                <div className="flex items-center justify-between gap-3">
                                  <div className="text-sm font-bold text-slate-900">{card.name}</div>
                                  {note ? <div className="text-[11px] text-slate-500">{note}</div> : null}
                                </div>

                                <div className="mt-2 text-sm text-slate-800 whitespace-pre-line leading-relaxed">
                                  {text}
                                </div>

                                {note ? (
                                  <div className="mt-2 text-[11px] text-slate-500 sm:hidden">{note}</div>
                                ) : null}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            )}

            <div className="mt-8 rounded-2xl bg-white/70 px-4 py-4 text-[11px] sm:text-xs text-slate-600">
              <div className="mb-1 font-semibold text-slate-800">※ 주의 사항</div>
              <ul className="space-y-1">
                <li className="font-semibold text-slate-800">– 결제금액 5만원 이상부터 적용됩니다.</li>
                <li>– 현대카드는 비즈니스 프랜차이즈 결제에서만 가능합니다.</li>
                <li>
                  – 제외카드(체크, 선불, 법인, 기프트 하이브리드, 은행계열 등)는 무이자 할부 적용이
                  제한될 수 있습니다.
                </li>
                <li>– 하나카드는 할부가 불가합니다.</li>
                <li>
                  – 은행계열카드 : 카드 전·후면에 BC로고(마크)가 없는 카드는 예) 수협, 제주 등 일부
                  무이자 적용이 제한될 수 있습니다.
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-16">
        <div className="mx-auto max-w-5xl px-4">
          <div className="rounded-3xl border border-slate-200 bg-white px-6 py-10 shadow-sm">
            <div className="text-center text-lg sm:text-xl font-extrabold text-slate-900">
              주류페이에 대해 궁금한 점을 확인해보세요.
            </div>

            <div className="mt-8 space-y-4">
              {FAQ_ITEMS.map((item, idx) => (
                <div key={idx} className="space-y-2">
                  <div className="flex items-center gap-3 rounded-2xl bg-slate-100 px-4 py-3">
                    <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-orange-500 text-white text-xs font-extrabold">
                      Q
                    </span>
                    <div className="text-sm font-semibold text-slate-900">{item.q}</div>
                  </div>
                  <div className="flex items-start gap-3 rounded-2xl bg-[#fff7ed] px-4 py-3">
                    <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#ea580c] text-white text-xs font-extrabold">
                      W
                    </span>
                    <div className="text-sm text-slate-900 leading-relaxed">{item.a}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 flex justify-center">
              <button
                type="button"
                onClick={() => nav("/faq")}
                className="btn-outline rounded-full px-6 py-2 text-sm"
              >
                자주 묻는 질문
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#050816] py-16">
        <div className="mx-auto max-w-5xl px-4">
          <div className="rounded-3xl bg-gradient-to-r from-slate-900 to-slate-800 px-6 py-10 text-center text-white shadow-2xl">
            <h2 className="text-xl sm:text-2xl font-semibold">지금 바로 간편 결제를 시작해보세요</h2>
            <p className="mt-3 text-sm text-slate-200">회원가입 → 요청 생성 → 파일 업로드 → 승인 → 결제완료</p>

            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-4">
              <button
                type="button"
                onClick={() => nav("/signup")}
                className="btn-primary inline-flex min-w-[140px] px-6 py-3 text-sm active:translate-y-px"
              >
                시작하기
              </button>
              <button
                type="button"
                onClick={() => nav("/login")}
                className="btn-outline inline-flex min-w-[140px] border-slate-500 px-6 py-3 text-sm text-slate-100 hover:bg-slate-800"
              >
                이미 계정이 있어요
              </button>
            </div>
          </div>

          <footer className="mt-12 text-xs text-slate-400">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <div className="font-semibold text-slate-200">JULYU pay</div>
                <p className="mt-1">주소 : 전북특별자치도 익산시 동천7길 72 카이로스빌딩 3층</p>
                <p className="mt-1">
                  회사명 : 주류페이 | 대표자 : 강대부 | 사업자등록번호 : 518880368 | 통신판매업신고번호 :
                  2024-전북익산-0474
                </p>
                <p className="mt-1">전화상담 : 1899-7179 (연중무휴 09~24시) | 메일상담 : julyupay@gmail.com</p>
                <p className="mt-1">Copyright © 2024. All rights reserved.</p>
              </div>

              <div className="flex flex-col gap-2 sm:items-end">
                <button
                  type="button"
                  onClick={() => nav("/terms")}
                  className="rounded-full border border-slate-600 px-3 py-1.5 text-[11px] text-slate-100 hover:bg-slate-800"
                >
                  서비스 이용약관
                </button>
                <button
                  type="button"
                  onClick={() => nav("/privacy")}
                  className="rounded-full border border-slate-600 px-3 py-1.5 text-[11px] text-slate-100 hover:bg-slate-800"
                >
                  개인정보 처리방침
                </button>
              </div>
            </div>
          </footer>
        </div>
      </section>

      <KakaoChatButton />
      <HomeNoticeModal />
    </div>
  );
}
